import {Alert} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {useWatch, type UseFormReturn} from 'react-hook-form';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {
  ConfiguratorBundleResponseBody,
  EvaluateConfigurationResponseBody,
} from '@dms/api/new-vehicle';
import {BasisPrice, SelectableCard, useNewVehicleCatalogueLabels} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';

import {formatBatteryCapacity} from '../../../NewVehicleCatalogue/formatConsumptionAndEmission';
import type {ConfiguratorFormValues} from '../../NewVehicleConfigurator';
import {getTrimEngines} from '../../utils/getTrimEngines';
import {SectionLabel, StepIntro} from '../StepIntro';

export type EngineDriveProps = {
  bundle?: ConfiguratorBundleResponseBody;
  trimId: string;
  form: UseFormReturn<ConfiguratorFormValues>;
  evaluation?: EvaluateConfigurationResponseBody;
  canSelect: boolean;
  factoryFreezeReason?: string;
};

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});

export function EngineDrive(props: EngineDriveProps) {
  const variantId = useWatch({control: props.form.control, name: 'variantId'});
  const labels = useNewVehicleCatalogueLabels();
  const trimEngines = getTrimEngines(props.bundle, props.trimId);

  return (
    <VStack spacing={6}>
      <StepIntro
        title={t('entity.newVehicle.steps.engineDriveTitle', 'Step 2 — Engine & drive')}
        description={t(
          'entity.newVehicle.steps.engineDriveIntro',
          'Pick the drivetrain for this trim level. The price shown is the trim + engine base price.'
        )}
      />
      <VStack spacing={3}>
        <SectionLabel
          label={t('entity.newVehicle.labels.engineDrive', 'Engine & drive')}
          count={trimEngines.length}
        />
        <VStack spacing={2}>
          <Show when={props.bundle}>
            {(bundle) =>
              trimEngines.map(({variant, engine}) => (
                <SelectableCard
                  key={variant.id}
                  control="radio"
                  name="variant"
                  title={engine.name}
                  isSelected={variant.id === variantId}
                  isDisabled={!props.canSelect || Boolean(props.factoryFreezeReason)}
                  isRequired
                  reason={props.factoryFreezeReason}
                  onSelect={() => props.form.setValue('variantId', variant.id, {shouldDirty: true})}
                  description={[
                    `${engine.powerKw} kW`,
                    labels.formatFuel(engine),
                    labels.getTransmission(engine.transmission),
                    labels.getDrive(engine.drive),
                    formatBatteryCapacity(engine.technicalData),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  trailing={
                    <BasisPrice
                      net={variant.priceNet}
                      gross={variant.priceGross}
                      basis={bundle.priceBasis}
                      currency={bundle.currency}
                      alternative
                      noWrap
                      align="right"
                    />
                  }
                />
              ))
            }
          </Show>
        </VStack>
      </VStack>
      <Show when={isNotNilOrEmpty(props.evaluation?.invalidatedSelectionIds)}>
        <Alert
          variant="warning"
          message={t(
            'entity.newVehicle.notifications.selectionsDroppedForEngine',
            'Selections removed because they are not available with this engine'
          )}
        />
      </Show>
    </VStack>
  );
}
